<script setup lang="ts">
import { inject, reactive, Ref, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { usePreferredManagementStore } from '@/store/PreferredManagementStore';
import { useToast } from 'primevue/usetoast';
import Button from 'primevue/button';
import DatePicker from 'primevue/datepicker';
import Fluid from 'primevue/fluid';

const dialogRef = inject('dialogRef') as Ref;
const code = dialogRef?.value?.data?.qId;
const status = dialogRef?.value?.data?.sts;
const type = dialogRef?.value?.data?.type;
const isSave = dialogRef?.value?.options?.emits?.isSave;
const renderTable = dialogRef?.value?.options?.emits?.renderTable;

const { t } = useI18n({ useScope: 'global' });
const toast = useToast();
const preferredManagementStore = usePreferredManagementStore();
const preferredForm = reactive({
  startDate: null,
  endDate: null
});
const minDateRangeValue = ref(new Date());

const formatDate = (date) => {
  return date ? DateTime.fromJSDate(new Date(date)).toFormat('yyyy-LL-dd') : '';
};
const checkIsBeforeDate = (startDate, endDate) => {
  const sDate = DateTime.fromJSDate(new Date(startDate));
  const eDate = DateTime.fromJSDate(new Date(endDate));
  return eDate > sDate;
};
const onSave = async () => {
  if (checkIsBeforeDate(preferredForm.startDate, preferredForm.endDate)) {
    const payload = {
      startDate: formatDate(preferredForm.startDate),
      endDate: formatDate(preferredForm.endDate),
      sts: status
    };
    if (code) {
      await preferredManagementStore
        .handleUpdateStatusPreferred(code, type, payload)
        .then(() => {
          toast.add({ severity: 'success', summary: '', detail: t('notification.save_successfully'), life: 3000 });
          isSave();
          renderTable();
        })
        .catch((err) => {
          toast.add({ severity: 'error', summary: '', detail: err?.message, life: 5000 });
        });
    }
  } else {
    toast.add({ severity: 'error', summary: '', detail: t('note.the_end_date_must_be_after') });
  }
};
</script>
<template>
  <div class="mt-5">
    <Fluid>
      <div class="field grid" v-if="preferredForm">
        <div class="col">
          <label for="startDate">{{ $t('create_quote.start_date') }}</label>
          <DatePicker id="startDate" v-model="preferredForm.startDate" :minDate="minDateRangeValue" dateFormat="dd-M-yy">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </div>
        <div class="col">
          <label for="endDate">{{ $t('create_quote.end_date') }}</label>
          <DatePicker id="endDate" v-model="preferredForm.endDate" :minDate="minDateRangeValue" dateFormat="dd-M-yy">
            <template #footer>
              <p class="text-center">
                <small>{{ $t('common.press_space') }}</small>
              </p>
            </template>
          </DatePicker>
        </div>
      </div>
    </Fluid>
  </div>
  <div class="text-center">
    <Button id="savePreferred" :disabled="!preferredForm?.startDate || !preferredForm?.endDate" @click="onSave()">{{ $t('common.save') }}</Button>
  </div>
</template>
